import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Globe, Video, Coins, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

// X → GlobalEyes landing funnel. /x/welcome?src=<list-or-campaign-slug>
// Stores the source; attribution is posted after signup (App-level effect
// in FloatingRecorderFAB) so the growth agent can report conversion.
export default function XLandingPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const src = useMemo(() => (params.get("src") || "x-general").slice(0, 64), [params]);
  const [stored, setStored] = useState(false);

  useEffect(() => {
    try { localStorage.setItem("ge_x_src", src); setStored(true); } catch { setStored(false); }
  }, [src]);

  const openSignup = () => {
    if (user) { navigate("/dashboard"); return; }
    window.dispatchEvent(new CustomEvent("open-auth", { detail: { mode: "register" } }));
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col items-center px-6 pt-16 pb-24" data-testid="x-landing-page">
      <div className="flex items-center gap-2 text-emerald-400 mb-6">
        <Globe className="h-8 w-8" />
        <span className="text-2xl font-bold tracking-tight">GlobalEyes.TV</span>
      </div>
      <h1 className="text-4xl sm:text-5xl font-bold text-center leading-tight max-w-xl">
        The world, broadcast by people like you.
      </h1>
      <p className="text-gray-400 text-base text-center max-w-md mt-4">
        You found us on X{src !== "x-general" ? ` (via ${src})` : ""}. Members earn
        credits for filming, sharing, and even for following us back on X.
      </p>
      <div className="grid gap-4 mt-10 w-full max-w-md">
        {[
          { icon: Video, title: "Film what's around you", text: "Short geolocated broadcasts, pinned to the world map." },
          { icon: Coins, title: "Earn real credits", text: "Tips, applause, paid commission offers, and share rewards." },
          { icon: MapPin, title: "Old-school broadcast soul", text: "A public-broadcasting feel — signal over noise." },
        ].map(({ icon: Icon, title, text }) => (
          <div key={title} className="flex gap-3 bg-gray-900 border border-gray-800 rounded-xl p-4">
            <Icon className="h-6 w-6 text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">{title}</p>
              <p className="text-sm text-gray-400">{text}</p>
            </div>
          </div>
        ))}
      </div>
      <Button
        onClick={openSignup}
        className="mt-10 bg-emerald-500 hover:bg-emerald-400 text-gray-950 font-bold text-lg px-10 py-6 rounded-full"
        data-testid="x-landing-signup-btn"
      >
        {user ? "Go to your dashboard" : "Claim your free credits"}
      </Button>
      <button
        onClick={() => navigate("/")}
        className="mt-4 text-sm text-gray-500 hover:text-gray-300 underline underline-offset-4"
        data-testid="x-landing-watch-first"
      >
        Just watch first
      </button>
      {stored && <span className="sr-only" data-testid="x-src-stored">{src}</span>}
    </div>
  );
}
